/* Feb-2026 · Auto-Ledger Postings health monitor
 *
 * Read-only monitor of the auto-post bridges (Stripe fulfilment,
 * cost_events). Shows recency stats per source and any unbridged
 * rows so the bookkeeper knows exactly which manual JE to post
 * next.
 * Backend: GET /api/admin/gl/auto-postings/health, /gaps
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { PanelHeader } from './_shared';
import { LedgerAccessLink } from './LedgerAccessLink';
import { Activity, RefreshCw, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

const SOURCE_LABEL = {
  stripe_webhook: 'Stripe checkouts',
  cost_event:     'LLM / infra costs',
  ledger:         'Manual ledger posts',
  payable:        'Vendor payables',
  reversal:       'Reversals / voids',
  inventory:      'Inventory moves',
  manual_je:      'Manual JE',
};

export default function AutoLedgerHealthPanel() {
  const [health, setHealth] = useState(null);
  const [gaps, setGaps] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    setBusy(true);
    try {
      const [h, g] = await Promise.all([
        http.get('/admin/gl/auto-postings/health'),
        http.get('/admin/gl/auto-postings/gaps'),
      ]);
      setHealth(h.data);
      setGaps(g.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Health load failed.');
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const cov = health?.coverage || {};
  return (
    <div className="space-y-4" data-testid="auto-ledger-health-panel">
      <PanelHeader
        icon={Activity}
        title="Auto-Ledger Health"
        subtitle="Stripe checkouts and LLM cost events auto-post to the general ledger. This monitor lists coverage and any gaps."
      />
      <div className="flex justify-end"><LedgerAccessLink testId="auto-ledger-open-ledger" /></div>
      <Button size="sm" variant="outline" onClick={reload} disabled={busy} data-testid="auto-ledger-refresh">
        {busy ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-2 h-3.5 w-3.5" />}
        Refresh
      </Button>

      {health && (
        <div className={`rounded-md border p-3 ${health.healthy ? 'border-emerald-200 bg-emerald-50' : 'border-rose-300 bg-rose-50'}`}>
          <div className="flex items-center gap-2 text-[13px] font-semibold">
            {health.healthy ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 text-rose-600" />}
            {health.healthy ? 'All bridges healthy — every $ row has a matching JE.' : 'Bridge gaps detected — some $ rows have no JE.'}
          </div>
          <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
            <div className="rounded-md border border-current/20 bg-white/60 p-2 text-[12px]" data-testid="auto-ledger-cov-costs">
              <div className="font-semibold">Cost events</div>
              <div>{cov.cost_events?.total ?? 0} total · {cov.cost_events?.missing_journal ?? 0} without JE</div>
              <div className="text-[11px] text-muted-foreground">{cov.cost_events?.percent_covered ?? 0}% covered</div>
            </div>
            <div className="rounded-md border border-current/20 bg-white/60 p-2 text-[12px]" data-testid="auto-ledger-cov-stripe">
              <div className="font-semibold">Stripe transactions</div>
              <div>{cov.stripe_transactions?.total ?? 0} total · {cov.stripe_transactions?.missing_journal ?? 0} without JE</div>
              <div className="text-[11px] text-muted-foreground">{cov.stripe_transactions?.percent_covered ?? 0}% covered</div>
            </div>
          </div>
        </div>
      )}

      {health?.sources?.length > 0 && (
        <div className="rounded-md border border-border bg-card p-3">
          <div className="mb-2 text-[11px] uppercase tracking-wide text-muted-foreground">Recent JE volume (last 7 days)</div>
          <table className="w-full text-[12px]">
            <thead className="text-[10px] uppercase text-muted-foreground">
              <tr>
                <th className="px-2 py-1 text-left">Source</th>
                <th className="px-2 py-1 text-right">24h</th>
                <th className="px-2 py-1 text-right">7 days</th>
                <th className="px-2 py-1 text-right">Last posted</th>
              </tr>
            </thead>
            <tbody>
              {health.sources.map((s) => (
                <tr key={s.source} className="border-t border-border" data-testid={`auto-ledger-source-${s.source}`}>
                  <td className="px-2 py-1">{SOURCE_LABEL[s.source] || s.source}</td>
                  <td className="px-2 py-1 text-right font-mono-stat">{s.count_24h}</td>
                  <td className="px-2 py-1 text-right font-mono-stat">{s.count_7d}</td>
                  <td className="px-2 py-1 text-right text-[11px] text-muted-foreground">{s.last_posted ? new Date(s.last_posted).toLocaleString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(gaps?.cost_event_gaps?.length > 0 || gaps?.stripe_gaps?.length > 0) && (
        <div className="rounded-md border border-rose-200 bg-rose-50 p-3" data-testid="auto-ledger-gaps">
          <div className="mb-2 text-[13px] font-semibold text-rose-900">Bridge gaps — post a manual JE via the Ledger panel</div>
          {gaps.cost_event_gaps.length > 0 && (
            <div className="mb-3">
              <div className="text-[11px] uppercase tracking-wide text-rose-900/70">Cost events</div>
              <ul className="mt-1 text-[11.5px]">
                {gaps.cost_event_gaps.slice(0, 8).map((g) => (
                  <li key={g.id}>{g.provider}/{g.model || g.kind} · ${(g.base_cost_usd || 0).toFixed(4)} · {g.ts}</li>
                ))}
              </ul>
            </div>
          )}
          {gaps.stripe_gaps.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-rose-900/70">Stripe transactions</div>
              <ul className="mt-1 text-[11.5px]">
                {gaps.stripe_gaps.slice(0, 8).map((g) => (
                  <li key={g.id || g.session_id}>{g.kind || 'checkout'} · ${((g.amount_total_cents || 0) / 100).toFixed(2)} · user {g.user_id}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
